import { createElement, type ComponentType } from "react";
import { PERMISSIONS } from "@/shared/consts";
import {
  HR_APPLY_PAGE_ROUTE,
  HR_CONTRACTS_PAGE_ROUTE,
  HR_EMPLOYEES_PAGE_ROUTE,
  HR_IMPORT_WORKER_PAGE_ROUTE,
  HR_WORKER_DETAIL_PAGE_ROUTE,
  PROJECTS_VOLUNTEERS_PAGE_ROUTE,
  HR_HIRING_PAGE_ROUTE,
  HR_EXPERTS_PAGE_ROUTE,
  HR_LEAVES_PAGE_ROUTE,
  LOGIN_PAGE_ROUTE,
  DEMO_CREATE_ROUTE,
  CHANGE_PASSWORD_PAGE_ROUTE,
  FORGOT_PASSWORD_PAGE_ROUTE,
  RESET_PASSWORD_PAGE_ROUTE,
  PRIVACY_POLICY_PAGE_ROUTE,
  TERMS_OF_USE_PAGE_ROUTE,
  HR_FILL_CONTRACT_PAGE_ROUTE,
  HR_EDIT_CONTRACT_PAGE_ROUTE,
  LEGAL_TEMPLATES_PAGE_ROUTE,
  LEGAL_APPLICATIONS_PAGE_ROUTE,
  GUEST_SIGNING_PAGE_ROUTE,
  GUEST_SIGNING_TOKEN_PAGE_ROUTE,
  SIGNING_DONE_PAGE_ROUTE,
  SIGNING_INVITATION_PAGE_ROUTE,
  LEGAL_ATTACHED_DOCUMENTS_PAGE_ROUTE,
  ACCOUNTING_TIMESHEETS_PAGE_ROUTE,
  ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE,
  ACCOUNTING_PURCHASES_PAGE_ROUTE,
  ACCOUNTING_WAREHOUSE_PAGE_ROUTE,
  ACCOUNTING_WAREHOUSE_REQUESTS_PAGE_ROUTE,
  ACCOUNTING_FIXED_ASSETS_PAGE_ROUTE,
  ACCOUNTING_DEPRECIATION_PAGE_ROUTE,
  WAREHOUSE_PUBLIC_UNIT_PAGE_ROUTE,
  ACCOUNTING_REPORTS_PAGE_ROUTE,
  ACCOUNTING_FINANCIAL_REPORTS_PAGE_ROUTE,
  ACCOUNTING_EQUITY_CHANGES_REPORT_PAGE_ROUTE,
  ACCOUNTING_TAX_ROUTE,

  ACCOUNTING_INCOME_PAGE_ROUTE,
  ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE,
  ACCOUNTING_CONTRACTOR_PAYROLL_DETAIL_PAGE_ROUTE,
  FINANCE_PAGE_ROUTE,
  PROFILE_PAGE_ROUTE,

  REQUESTS_PAGE_ROUTE,
  REQUESTS_ACTIVE_PAGE_ROUTE,
  REQUESTS_HISTORY_PAGE_ROUTE,
  CALCULATIONS_PAGE_ROUTE,
  PENDING_ACTIONS_PAGE_ROUTE,
  PROJECTS_PAGE_ROUTE,
  PROJECT_DETAIL_ROUTE,
  PROJECT_MONITORING_ROUTE,
  PROJECT_REPORTS_ROUTE,
  PROJECT_REPORT_PREVIEW_ROUTE,
  ANNUAL_REPORTS_ROUTE,
  ANNUAL_REPORT_PREVIEW_ROUTE,
  ESG_REPORTS_ROUTE,
  ESG_REPORT_PREVIEW_ROUTE,
  SROI_ROUTE,
  SROI_PROJECT_ROUTE,
  SROI_SURVEY_PAGE_ROUTE,
  BOARD_PAGE_ROUTE,
  FORMS_PAGE_ROUTE,
  CAMPAIGNS_PAGE_ROUTE,
  CAMPAIGN_PAGE_ROUTE,
  SURVEY_PUBLIC_PAGE_ROUTE,
  BANKING_PAGE_ROUTE,
  ORGANIZATION_PAGE_ROUTE,
  CHAT_PAGE_ROUTE,
  CHAT_ADMIN_PAGE_ROUTE,
  COMMUNITY_PAGE_ROUTE,
  REGISTRY_PROJECTS_PAGE_ROUTE,
  REGISTRY_ORGANIZATIONS_PAGE_ROUTE,
  REGISTRY_ORGANIZATION_PAGE_ROUTE,
  REGISTRY_PROJECT_PAGE_ROUTE,
  REGISTRY_PUBLICITY_PAGE_ROUTE,
  ACCOUNTING_PROJECT_TABLE_PAGE_ROUTE,
  ACCOUNTING_ADVANCES_PAGE_ROUTE,
  SUPPORT_PAGE_ROUTE,
} from "@/shared/utils";
import { runtimeConfig, isInternalAccountingEnabled } from "@/shared/config/runtime";
import { EmployeesPage } from "@/features/hr/employees";
import ProfilePage from "@/features/self/profile/pages/ProfilePage";
import { lazyPage } from "./lazyPage";

const LoginPage = lazyPage(() => import("@/features/auth").then((m) => ({ default: m.LoginPage })), ["LoginPage"]);
const ChangePasswordPage = lazyPage(() => import("@/features/auth").then((m) => ({ default: m.ChangePasswordPage })), ["ChangePasswordPage"]);
const ForgotPasswordPage = lazyPage(() => import("@/features/auth").then((m) => ({ default: m.ForgotPasswordPage })), ["ForgotPasswordPage"]);
const ResetPasswordPage = lazyPage(() => import("@/features/auth").then((m) => ({ default: m.ResetPasswordPage })), ["ResetPasswordPage"]);
const PrivacyPolicyPage = lazyPage(() => import("@/features/auth").then((m) => ({ default: m.PrivacyPolicyPage })), ["PrivacyPolicyPage"]);
const TermsOfUsePage = lazyPage(() => import("@/features/auth").then((m) => ({ default: m.TermsOfUsePage })), ["TermsOfUsePage"]);
const ImportWorkerPage = lazyPage(() => import("@/features/hr/employees/pages/ImportWorkerPage"), ["ImportWorkerPage"]);
const WorkerDetailPage = lazyPage(() => import("@/features/hr/employees/pages/WorkerDetailPage"), ["WorkerDetailPage"]);
const VolunteersPage = lazyPage(() => import("@/features/hr/volunteers").then((m) => ({ default: m.VolunteersPage })), ["VolunteersPage"]);
const HiringPage = lazyPage(() => import("@/features/hr/hiring").then((m) => ({ default: m.HiringPage })), ["HiringPage"]);
const ExpertsPage = lazyPage(() => import("@/features/hr/experts").then((m) => ({ default: m.ExpertsPage })), ["ExpertsPage"]);
const ApplyApplicationPage = lazyPage(() => import("@/features/hr/hiring").then((m) => ({ default: m.ApplyApplicationPage })), ["HiringPage"]);
const ContractsListPage = lazyPage(() => import("@/features/hr/contracts").then((m) => ({ default: m.ContractsListPage })), ["ContractsPage"]);
const FillContractPage = lazyPage(() => import("@/features/hr/contracts").then((m) => ({ default: m.FillContractPage })), ["ContractsPage", "FillContractPage"]);
const LeaveApplicationsPage = lazyPage(() =>
  import("@/features/hr/leave-applications").then((m) => ({ default: m.LeaveApplicationsPage })),
  ["LeaveApplicationsPage"]);
const LegalTemplatesPage = lazyPage(() => import("@/features/legal").then((m) => ({ default: m.LegalTemplatesPage })), ["AttachedDocumentsPage", "LegalApplicationsPage", "LegalConsultationsPage", "LegalTemplatesPage"]);
const LegalApplicationsPage = lazyPage(() => import("@/features/legal").then((m) => ({ default: m.LegalApplicationsPage })), ["AttachedDocumentsPage", "LegalApplicationsPage", "LegalConsultationsPage", "LegalTemplatesPage"]);
const AttachedDocumentsPage = lazyPage(() => import("@/features/legal").then((m) => ({ default: m.AttachedDocumentsPage })), ["AttachedDocumentsPage"]);
const GuestSigningPage = lazyPage(() => import("@/features/signing").then((m) => ({ default: m.GuestSigningPage })), ["GuestSigningPage"]);
const SigningDonePage = lazyPage(() => import("@/features/signing").then((m) => ({ default: m.SigningDonePage })), []);
const SigningInvitationPage = lazyPage(() => import("@/features/signing").then((m) => ({ default: m.SigningInvitationPage })), []);
const TimesheetsPage = lazyPage(() => import("@/features/accounting/timesheets").then((m) => ({ default: m.TimesheetsPage })), ["TimesheetsPage"]);
const TimesheetDetailPage = lazyPage(() =>
  import("@/features/accounting/timesheets").then((m) => ({ default: m.TimesheetDetailPage })),
  ["SmetaPage", "TimesheetsPage"]);
const ContractorPayrollListPage = lazyPage(() =>
  import("@/features/accounting/contractor-payroll").then((m) => ({ default: m.ContractorPayrollListPage })),
  ["ContractorPayrollPage"]);
const ContractorPayrollDetailPage = lazyPage(() =>
  import("@/features/accounting/contractor-payroll").then((m) => ({ default: m.ContractorPayrollDetailPage })),
  ["ContractorPayrollPage", "SmetaPage"]);
const PurchasesPage = lazyPage(() => import("@/features/accounting/purchases").then((m) => ({ default: m.PurchasesPage })), ["PurchasesPage", "SmetaPage"]);
const AdvancesPage = lazyPage(() => import("@/features/accounting/advances").then((m) => ({ default: m.AdvancesPage })), ["AdvancesPage"]);
const WarehousePage = lazyPage(() => import("@/features/accounting/warehouse").then((m) => ({ default: m.WarehousePage })), ["WarehousePage"]);
const WarehouseRequestsPage = lazyPage(() =>
  import("@/features/accounting/warehouse").then((m) => ({ default: m.WarehouseRequestsPage })),
  ["WarehousePage", "WarehouseRequestsPage"]);
const FixedAssetsPage = lazyPage(() => import("@/features/accounting/warehouse").then((m) => ({ default: m.FixedAssetsPage })), ["FixedAssetsPage", "WarehousePage"]);
const PublicUnitPage = lazyPage(() => import("@/features/accounting/warehouse").then((m) => ({ default: m.PublicUnitPage })), ["WarehousePage"]);
const DepreciationPage = lazyPage(() => import("@/features/accounting/warehouse").then((m) => ({ default: m.DepreciationPage })), ["DepreciationPage", "WarehousePage"]);
const FinancialReportPage = lazyPage(() =>
  import("@/features/accounting/financial-reports").then((m) => ({ default: m.FinancialReportPage })),
  ["FinancialReportPage"]);
const EquityChangesReportPage = lazyPage(() =>
  import("@/features/accounting/financial-reports").then((m) => ({ default: m.EquityChangesReportPage })),
  ["FinancialReportPage"]);
const ReportsPage = lazyPage(() => import("@/features/accounting/reports").then((m) => ({ default: m.ReportsPage })), ["ReportsPage"]);
const TaxPage = lazyPage(() => import("@/features/accounting/tax").then((m) => ({ default: m.TaxPage })), ["TaxPage"]);
const IncomePage = lazyPage(() => import("@/features/accounting/income").then((m) => ({ default: m.IncomePage })), ["IncomePage"]);
const ProjectTablePage = lazyPage(() => import("@/features/finance").then((m) => ({ default: m.ProjectTablePage })), ["FinancePage", "ProjectTablePage"]);
const FinancePage = lazyPage(() => import("@/features/finance").then((m) => ({ default: m.FinancePage })), ["FinancePage", "ProjectTablePage", "ProjectsPage"]);
const RequestsPage = lazyPage(() => import("@/features/self").then((m) => ({ default: m.RequestsPage })), ["AdvancesPage"]);
const CalculationsPage = lazyPage(() => import("@/features/self").then((m) => ({ default: m.CalculationsPage })), []);
const PendingActionsPage = lazyPage(() => import("@/features/self").then((m) => ({ default: m.PendingActionsPage })), ["PendingActionsPage"]);
const SupportPage = lazyPage(
  () =>
    import("@/features/support/pages/SupportPage").then((m) => ({
      default: function SupportRoutePage() {
        return createElement(m.default, { routes: privateRoutes });
      },
    })),
  ["SupportPage"],
);
// import { FSMMyReportsPage } from "@/features/self/fsm-reports";
const ProjectsPage = lazyPage(() => import("@/features/projects").then((m) => ({ default: m.ProjectsPage })), ["ProjectsPage", "TasksPage"]);
const ProjectDetailPage = lazyPage(() => import("@/features/projects").then((m) => ({ default: m.ProjectDetailPage })), ["ProjectsPage", "TasksPage", "CampaignsPage", "SurveysPage"]);
const ProjectMonitoringPage = lazyPage(() => import("@/features/projects").then((m) => ({ default: m.ProjectMonitoringPage })), ["ProjectMonitoringPage", "ProjectsPage", "TasksPage"]);
const ProjectReportsPage = lazyPage(() => import("@/features/projects").then((m) => ({ default: m.ProjectReportsPage })), ["ProjectsPage", "TasksPage"]);
const CreativeReportPreviewPage = lazyPage(() =>
  import("@/features/projects").then((m) => ({ default: m.CreativeReportPreviewPage })),
  ["ProjectsPage", "TasksPage"]);
const AnnualReportsPage = lazyPage(() => import("@/features/projects").then((m) => ({ default: m.AnnualReportsPage })), ["ProjectsPage", "TasksPage"]);
const AnnualReportPreviewPage = lazyPage(() =>
  import("@/features/projects").then((m) => ({ default: m.AnnualReportPreviewPage })),
  ["ProjectsPage", "TasksPage"]);
const EsgReportsPage = lazyPage(() => import("@/features/reporting").then((m) => ({ default: m.EsgReportsPage })), ["ReportingPage"]);
const EsgReportPreviewPage = lazyPage(() => import("@/features/reporting").then((m) => ({ default: m.EsgReportPreviewPage })), ["ReportingPage"]);
const SroiProjectsPage = lazyPage(() => import("@/features/sroi").then((m) => ({ default: m.SroiProjectsPage })), ["SroiPage"]);
const SroiProjectPage = lazyPage(() => import("@/features/sroi").then((m) => ({ default: m.SroiProjectPage })), ["SroiPage"]);
const SurveyPage = lazyPage(() => import("@/features/sroi").then((m) => ({ default: m.SurveyPage })), ["SurveyPage"]);
const BoardPage = lazyPage(() => import("@/features/tasks").then((m) => ({ default: m.BoardPage })), ["BoardPage", "ProjectsPage", "TasksPage", "SurveysPage"]);
const FormsLibraryPage = lazyPage(() => import("@/features/forms").then((m) => ({ default: m.FormsLibraryPage })), ["FormsLibraryPage"]);
const CampaignsPage = lazyPage(() => import("@/features/campaigns").then((m) => ({ default: m.CampaignsPage })), ["CampaignsPage", "Tags"]);
const CampaignPage = lazyPage(() => import("@/features/campaigns").then((m) => ({ default: m.CampaignPage })), ["CampaignsPage", "SurveysPage", "FormsLibraryPage", "Tags"]);
const SurveyPublicPage = lazyPage(() => import("@/features/surveys").then((m) => ({ default: m.SurveyPublicPage })), ["SurveyPublicPage", "FormsLibraryPage"]);
// import { FSMSubmissionsPage } from "@/features/fsm";
const BankingPage = lazyPage(() => import("@/features/banking").then((m) => ({ default: m.BankingPage })), ["BankingPage"]);
const MyOrganizationPage = lazyPage(() => import("@/features/organization").then((m) => ({ default: m.MyOrganizationPage })), ["MyOrganizationPage"]);
const ChatPage = lazyPage(() => import("@/features/chat").then((m) => ({ default: m.ChatPage })), ["ChatPage"]);
const CommunityPage = lazyPage(() => import("@/features/community").then((m) => ({ default: m.CommunityPage })), ["CommunityPage"]);
const ProjectsRegistryPage = lazyPage(() => import("@/features/registry").then((m) => ({ default: m.ProjectsRegistryPage })), ["RegistryPage", "ProjectsPage"]);
const OrganizationsRegistryPage = lazyPage(() => import("@/features/registry").then((m) => ({ default: m.OrganizationsRegistryPage })), ["RegistryPage", "ProjectsPage"]);
const RegistryOrganizationPage = lazyPage(() => import("@/features/registry").then((m) => ({ default: m.RegistryOrganizationPage })), ["RegistryPage", "ProjectsPage"]);
const RegistryProjectPage = lazyPage(() => import("@/features/registry").then((m) => ({ default: m.RegistryProjectPage })), ["RegistryPage", "ProjectsPage"]);
const PublicityPage = lazyPage(() => import("@/features/registry").then((m) => ({ default: m.PublicityPage })), ["RegistryPage", "ProjectsPage"]);
const ChatAdminPage = lazyPage(() => import("@/features/chat").then((m) => ({ default: m.ChatAdminPage })), ["ChatAdminPage"]);
const CreateDemoOrgPage = lazyPage(() => import("@/features/demo").then((m) => ({ default: m.CreateDemoOrgPage })), []);

export interface AppRoute {
  path: string;
  element: ComponentType;
  public?: boolean;
  /** Public route: don't bounce an already-authenticated user away. Private route: skip role/permission checks (still requires auth). */
  alwaysAccessible?: boolean;
  requiredPermissions?: string[];
  requiredRoles?: string[];
  requiredFeature?: string;
  enabled?: boolean;
}

export const publicRoutes: AppRoute[] = [
  {
    path: LOGIN_PAGE_ROUTE,
    element: LoginPage,
    public: true,
  },
  {
    path: PRIVACY_POLICY_PAGE_ROUTE,
    element: PrivacyPolicyPage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: TERMS_OF_USE_PAGE_ROUTE,
    element: TermsOfUsePage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: CHANGE_PASSWORD_PAGE_ROUTE,
    element: ChangePasswordPage,
    public: true,
  },
  {
    path: FORGOT_PASSWORD_PAGE_ROUTE,
    element: ForgotPasswordPage,
    public: true,
  },
  {
    path: RESET_PASSWORD_PAGE_ROUTE,
    element: ResetPasswordPage,
    public: true,
  },
  {
    path: HR_APPLY_PAGE_ROUTE,
    element: ApplyApplicationPage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: WAREHOUSE_PUBLIC_UNIT_PAGE_ROUTE,
    element: PublicUnitPage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: GUEST_SIGNING_PAGE_ROUTE,
    element: GuestSigningPage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: GUEST_SIGNING_TOKEN_PAGE_ROUTE,
    element: GuestSigningPage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: SIGNING_DONE_PAGE_ROUTE,
    element: SigningDonePage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: SIGNING_INVITATION_PAGE_ROUTE,
    element: SigningInvitationPage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: DEMO_CREATE_ROUTE,
    element: CreateDemoOrgPage,
    public: true,
    enabled: runtimeConfig.isDemo,
  },
  {
    path: SROI_SURVEY_PAGE_ROUTE,
    element: SurveyPage,
    public: true,
    alwaysAccessible: true,
  },
  {
    path: SURVEY_PUBLIC_PAGE_ROUTE,
    element: SurveyPublicPage,
    public: true,
    alwaysAccessible: true,
  },
];

export const privateRoutes: AppRoute[] = [
  {
    path: HR_EMPLOYEES_PAGE_ROUTE,
    element: EmployeesPage,
    requiredPermissions: [PERMISSIONS.HR.VIEW_EMPLOYEES],
  },
  {
    path: HR_IMPORT_WORKER_PAGE_ROUTE,
    element: ImportWorkerPage,
    requiredPermissions: [PERMISSIONS.HR.CREATE_EMPLOYEE],
  },
  {
    path: HR_WORKER_DETAIL_PAGE_ROUTE,
    element: WorkerDetailPage,
    requiredPermissions: [PERMISSIONS.HR.VIEW_EMPLOYEES],
  },
  {
    path: PROJECTS_VOLUNTEERS_PAGE_ROUTE,
    element: VolunteersPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_VOLUNTEERS],
  },
  {
    path: HR_HIRING_PAGE_ROUTE,
    element: HiringPage,
    requiredPermissions: [PERMISSIONS.HR.CREATE_EMPLOYEE],
  },
  {
    path: HR_EXPERTS_PAGE_ROUTE,
    element: ExpertsPage,
    requiredPermissions: [PERMISSIONS.HR.CREATE_EMPLOYEE],
    requiredFeature: "experts",
  },
  {
    path: HR_CONTRACTS_PAGE_ROUTE,
    element: ContractsListPage,
    requiredPermissions: [PERMISSIONS.HR.VIEW_CONTRACTS],
  },
  {
    path: HR_LEAVES_PAGE_ROUTE,
    element: LeaveApplicationsPage,
    requiredPermissions: [PERMISSIONS.HR.VIEW_LEAVE],
  },
  {
    path: HR_FILL_CONTRACT_PAGE_ROUTE,
    element: FillContractPage,
    requiredPermissions: [PERMISSIONS.HR.CREATE_CONTRACT],
  },
  {
    path: HR_EDIT_CONTRACT_PAGE_ROUTE,
    element: FillContractPage,
    requiredPermissions: [PERMISSIONS.HR.CREATE_CONTRACT],
  },
  {
    path: LEGAL_TEMPLATES_PAGE_ROUTE,
    element: LegalTemplatesPage,
    requiredPermissions: [PERMISSIONS.LEGAL.VIEW_AGREEMENTS],
  },
  {
    path: LEGAL_APPLICATIONS_PAGE_ROUTE,
    element: LegalApplicationsPage,
    requiredPermissions: [PERMISSIONS.LEGAL.VIEW_AGREEMENTS],
  },
  {
    path: LEGAL_ATTACHED_DOCUMENTS_PAGE_ROUTE,
    element: AttachedDocumentsPage,
    requiredPermissions: [PERMISSIONS.LEGAL.MANAGE_DOCUMENTS],
  },
  {
    path: ACCOUNTING_TIMESHEETS_PAGE_ROUTE,
    element: TimesheetsPage,
    requiredPermissions: [PERMISSIONS.HR.VIEW_TIMESHEETS],
  },
  {
    path: ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE,
    element: TimesheetDetailPage,
    requiredPermissions: [PERMISSIONS.HR.VIEW_TIMESHEETS],
  },
  {
    path: ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE,
    element: ContractorPayrollListPage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_GPH],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_CONTRACTOR_PAYROLL_DETAIL_PAGE_ROUTE,
    element: ContractorPayrollDetailPage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_GPH],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_PURCHASES_PAGE_ROUTE,
    element: PurchasesPage,
    requiredPermissions: [PERMISSIONS.WAREHOUSE.VIEW_INVOICES],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_WAREHOUSE_REQUESTS_PAGE_ROUTE,
    element: WarehouseRequestsPage,
    requiredPermissions: [PERMISSIONS.WAREHOUSE.VIEW_OWN_REQUESTS],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_DEPRECIATION_PAGE_ROUTE,
    element: DepreciationPage,
    requiredPermissions: [PERMISSIONS.WAREHOUSE.VIEW_REPORTS],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_FIXED_ASSETS_PAGE_ROUTE,
    element: FixedAssetsPage,
    requiredPermissions: [PERMISSIONS.WAREHOUSE.VIEW_INVENTORY],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_WAREHOUSE_PAGE_ROUTE,
    element: WarehousePage,
    requiredPermissions: [PERMISSIONS.WAREHOUSE.VIEW_INVENTORY],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_TAX_ROUTE,
    element: TaxPage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_TAX_REPORTS],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_FINANCIAL_REPORTS_PAGE_ROUTE,
    element: FinancialReportPage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_REPORTS],
  },
  {
    path: ACCOUNTING_EQUITY_CHANGES_REPORT_PAGE_ROUTE,
    element: EquityChangesReportPage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_REPORTS],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_REPORTS_PAGE_ROUTE,
    element: ReportsPage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_REPORTS],
  },
  {
    path: ACCOUNTING_INCOME_PAGE_ROUTE,
    element: IncomePage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_INCOME],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: FINANCE_PAGE_ROUTE,
    element: FinancePage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_BUDGET],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: PROFILE_PAGE_ROUTE,
    element: ProfilePage,
    requiredPermissions: [PERMISSIONS.SELF.VIEW_PROFILE],
  },
  {
    path: REQUESTS_PAGE_ROUTE,
    element: RequestsPage,
    requiredPermissions: [PERMISSIONS.SELF.APPLY_LEAVE],
  },
  {
    path: REQUESTS_ACTIVE_PAGE_ROUTE,
    element: RequestsPage,
    requiredPermissions: [PERMISSIONS.SELF.APPLY_LEAVE],
  },
  {
    path: REQUESTS_HISTORY_PAGE_ROUTE,
    element: RequestsPage,
    requiredPermissions: [PERMISSIONS.SELF.APPLY_LEAVE],
  },
  {
    path: CALCULATIONS_PAGE_ROUTE,
    element: CalculationsPage,
    requiredRoles: ["contractor", "msb_contractor"],
  },
  {
    path: PENDING_ACTIONS_PAGE_ROUTE,
    element: PendingActionsPage,
  },
  {
    path: SUPPORT_PAGE_ROUTE,
    element: SupportPage,
    alwaysAccessible: true,
  },
  {
    path: PROJECTS_PAGE_ROUTE,
    element: ProjectsPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
  {
    path: PROJECT_MONITORING_ROUTE,
    element: ProjectMonitoringPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_MONITORING],
  },
  {
    path: PROJECT_REPORT_PREVIEW_ROUTE,
    element: CreativeReportPreviewPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_REPORTS],
  },
  {
    path: PROJECT_REPORTS_ROUTE,
    element: ProjectReportsPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_REPORTS],
  },
  {
    path: ANNUAL_REPORT_PREVIEW_ROUTE,
    element: AnnualReportPreviewPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_ANNUAL_REPORTS],
  },
  {
    path: ANNUAL_REPORTS_ROUTE,
    element: AnnualReportsPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_ANNUAL_REPORTS],
  },
  {
    path: ESG_REPORT_PREVIEW_ROUTE,
    element: EsgReportPreviewPage,
    requiredPermissions: [PERMISSIONS.SUSTAINABILITY.VIEW_REPORTS],
  },
  {
    path: ESG_REPORTS_ROUTE,
    element: EsgReportsPage,
    requiredPermissions: [PERMISSIONS.SUSTAINABILITY.VIEW_REPORTS],
  },
  {
    path: SROI_PROJECT_ROUTE,
    element: SroiProjectPage,
    requiredPermissions: [PERMISSIONS.SROI.VIEW_ASSESSMENTS],
  },
  {
    path: SROI_ROUTE,
    element: SroiProjectsPage,
    requiredPermissions: [PERMISSIONS.SROI.VIEW_ASSESSMENTS],
  },
  {
    path: PROJECT_DETAIL_ROUTE,
    element: ProjectDetailPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
  {
    path: BOARD_PAGE_ROUTE,
    element: BoardPage,
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
  {
    path: FORMS_PAGE_ROUTE,
    element: FormsLibraryPage,
    requiredPermissions: [PERMISSIONS.FORMS.MANAGE, PERMISSIONS.PROJECTS.MANAGE_TASKS],
  },
  {
    path: CAMPAIGNS_PAGE_ROUTE,
    element: CampaignsPage,
    requiredPermissions: [PERMISSIONS.CAMPAIGNS.MANAGE],
  },
  {
    path: CAMPAIGN_PAGE_ROUTE,
    element: CampaignPage,
    requiredPermissions: [PERMISSIONS.CAMPAIGNS.MANAGE],
  },
  {
    path: ACCOUNTING_PROJECT_TABLE_PAGE_ROUTE,
    element: ProjectTablePage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_PROJECT_TABLE],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: ACCOUNTING_ADVANCES_PAGE_ROUTE,
    element: AdvancesPage,
    requiredPermissions: [PERMISSIONS.ACCOUNTING.VIEW_ADVANCES],
    enabled: isInternalAccountingEnabled,
  },
  {
    path: BANKING_PAGE_ROUTE,
    element: BankingPage,
    requiredPermissions: [PERMISSIONS.BANKING.VIEW_ACCOUNTS],
    enabled: runtimeConfig.enableBanking,
  },
  {
    path: ORGANIZATION_PAGE_ROUTE,
    element: MyOrganizationPage,
    requiredRoles: ["chief"],
  },
  // {
  //   path: FSM_SUBMISSIONS_PAGE_ROUTE,
  //   element: FSMSubmissionsPage,
  //   requiredPermissions: [PERMISSIONS.FSM.VIEW_ALL_REPORTS],
  // },
  // {
  //   path: FSM_MY_REPORTS_PAGE_ROUTE,
  //   element: FSMMyReportsPage,
  // },
  {
    path: CHAT_PAGE_ROUTE,
    element: ChatPage,
    enabled: runtimeConfig.enableChat,
  },
  {
    path: CHAT_ADMIN_PAGE_ROUTE,
    element: ChatAdminPage,
    requiredPermissions: [PERMISSIONS.CHAT.MANAGE_CHANNELS],
    enabled: runtimeConfig.enableChatAdmin,
  },
  {
    path: COMMUNITY_PAGE_ROUTE,
    element: CommunityPage,
    requiredFeature: "community",
  },
  {
    path: REGISTRY_PROJECTS_PAGE_ROUTE,
    element: ProjectsRegistryPage,
    requiredFeature: "registry",
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
  {
    path: REGISTRY_ORGANIZATIONS_PAGE_ROUTE,
    element: OrganizationsRegistryPage,
    requiredFeature: "registry",
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
  {
    path: REGISTRY_ORGANIZATION_PAGE_ROUTE,
    element: RegistryOrganizationPage,
    requiredFeature: "registry",
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
  {
    path: REGISTRY_PROJECT_PAGE_ROUTE,
    element: RegistryProjectPage,
    requiredFeature: "registry",
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
  {
    path: REGISTRY_PUBLICITY_PAGE_ROUTE,
    element: PublicityPage,
    requiredFeature: "registry",
    requiredPermissions: [PERMISSIONS.PROJECTS.VIEW_BOARD],
  },
];
